import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable, of, Subscription, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/common/components/base.component';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';
import {
  frontendSettingsErrorSelector,
  frontendSettingsIsLoadingSelector,
  frontendSettingsSelector,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import {
  IAppState,
  IFrontendSettings,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import * as FrontendSettingsActions from 'src/app/modules/shared/ngrx.stores/frontend.settings/actions';
import { TripPlannerService } from '../../services/trip.planner.service';
import {
  Itinerary,
  ItineraryAttraction,
  ItinerarySettings,
  MultiWidgetSettings,
} from '../../models/itinerary';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { ImageService } from 'src/app/common/services/image/image.service';
import { IMapMarkerWithCustomData } from 'src/app/modules/shared/models/place.marker';
import { VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import { VisitorEvent } from 'src/app/modules/shared/models/visitor.event';
import { GoogleMapComponent } from 'src/app/modules/shared/components/google.map/google.map.component';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { GoogleMapService } from 'src/app/modules/shared/services/google.map.service';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';
import { EventTimeService } from 'src/app/modules/event-calendar/services/event.time.service';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { removeHtmlTags } from 'src/app/common/utils/string.extensions';
import { routeConfigs } from '../../../../common/constants/route.config';
import { NgxColorsModule } from 'ngx-colors';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ColorPickerComponent } from 'src/app/common/components/color.picker/color.picker.component';
import { TextAndColorComponent } from '../../../../common/components/text.and.color/text.and.color.component';
import { CreateItineraryComponent } from './create.itinerary/create.itinerary.component';
import { MatIconModule } from '@angular/material/icon';
import { ItineraryImageUploaderComponent } from './itinerary.image.uploader/itinerary.image.uploader.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { clone } from '../../../../common/utils/object.extensions';
import { ExportPDFService } from '../../../shared/services/export.pdf.service';
import { ButtonTriggerEvent } from '../../../../common/components/button/button.component';
import { ItineraryDetailsModalComponent } from '../itinerary.details.popup/itinerary.details.modal.component';
import { SeoService } from '../../../../common/services/seo.service/seo.service';
import { VisitorService } from '../../../shared/services/visitor.service';
import { finalize } from 'rxjs/operators';
import { CalendarSharedModule } from 'src/app/modules/shared/calendar.shared.module';
import { MatMenuTrigger } from '@angular/material/menu';
import { ItineraryMultiWidgetsManagerComponent } from './itinerary.multi.widgets.manager/itinerary.multi.widgets.manager.component';

@Component({
  selector: 'ig-itinerary',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    NgxColorsModule,
    FormsModule,
    ColorPickerComponent,
    TextAndColorComponent,
    CreateItineraryComponent,
    MatIconModule,
    ItineraryImageUploaderComponent,
    ReactiveFormsModule,
    ItineraryDetailsModalComponent,
    CalendarSharedModule,
    ItineraryMultiWidgetsManagerComponent,
  ],
  templateUrl: './itinerary.component.html',
  styleUrl: './itinerary.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ItineraryComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isFromTripPlanner = false;

  isAuthenticated = true;
  isLoadingItineraries = false;
  isLoading$: Observable<boolean>;
  error$: Observable<string | null>;
  frontendSettings$: Observable<IFrontendSettings | null>;

  widgetItineraries: Itinerary[];
  allItineraries: Itinerary[];
  isUpdatingItineraries = false;
  isMobile = false;
  isEditMode = false;
  mapStyle: 'dark' | 'light' = 'dark';
  defaultMapIconUrl = `https://iti-images.s3.amazonaws.com/imgs/map-icon.png`;

  itinerarySettings: ItinerarySettings = null;
  widgetCode = 'default';
  itineraryMultiWidgetSettings: MultiWidgetSettings[] = [];

  isAtStart = false;
  isAtEnd = false;

  isForSingleItineraryEmbed = false;
  enteringItineraryId = null;
  isStartEntering = false;
  isStartQuiting = false;
  duration = 400;
  expandingAttractions: boolean[] = [];
  isFoldItineraryTitle = true;
  isAttractionsExpanded = false;

  bgImg: HTMLElement = null;
  imgPositionInListView;
  currentItinerary: Itinerary = null;
  isDisplayingDescription = false;
  isShowSaveAttractionChanges = false;
  mapMarkers: IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>[] = [];
  userMapMarkers: IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>[] = [];

  scrollListener = null;
  @ViewChild('listContainer', { static: false }) listContainer: ElementRef;
  @ViewChild('itineraryDescription', { static: false })
  itineraryDescription: ElementRef;
  @ViewChild('backgroundDiv', { static: false }) backgroundDiv: ElementRef;
  @ViewChild('mapmask', { static: false }) mapMask: ElementRef;
  @ViewChild(GoogleMapComponent, { static: false })
  googleMap: GoogleMapComponent;
  @ViewChildren('itinerarycard') itineraryCards: QueryList<ElementRef>;
  @ViewChildren('attractioncard') attractionCards: QueryList<ElementRef>;

  @ViewChild('storyMap', { static: false }) storyMap: ElementRef;

  @ViewChild('modeChangeMenuTrigger', { static: false })
  modeChangeMenuTrigger: MatMenuTrigger;
  @ViewChild('car360', { static: false }) car360: ElementRef;
  @ViewChild('mapContainer', { static: false }) mapContainer: ElementRef;
  @ViewChild('simulateTextContainer', { static: false })
  simulateTextContainer: ElementRef;
  @ViewChild('attractionPopup', { static: false })
  attractionPopup: ElementRef;

  carOffset = 30;
  carMovingSpeed = 1;
  isSimulateTourShowing = false;

  isMovieSimulateTourShowing = false;
  isMovieDisplayingAttractions = false;
  currentAttractionInTourSimulation: ItineraryAttraction = undefined;
  attractionIndexInTourSimulation = 0;
  attractionDisplayInTourSimulation = false;
  isAttractionQuiting = false;
  attractionPreInTourSimulation = false;
  isPreQuiting = false;
  attractionPostInTourSimulation = false;
  isPostQuiting = false;
  attractionConnectionInTourSimulation = false;
  isConnectionQuiting = false;
  attractionConnection = '';

  preloadImageCount = 3;
  directionResults: google.maps.DirectionsResult;
  directionLines: google.maps.Polyline[] = [];
  currentVideoType: 'youtube' | 'aerial' = 'aerial';

  link = '';
  popupLinkCopied = false;
  embedLinkCopied = false;
  itineraryDetailStyle: 'map' | 'story' | 'map3d' = 'map';

  isGeneratingPDF = false;
  modalCloseSubscription: Subscription;

  selectedWidgets: {
    [itineraryId: string]: { [widgetCode: string]: boolean };
  } = {};

  constructor(
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public mediaQueryService: MediaQueryService,
    public tripService: TripPlannerService,
    public seoService: SeoService,
    public visitorService: VisitorService,
    public imageService: ImageService,
    public modalService: ModalService,
    public googleMapService: GoogleMapService,
    public scriptService: ScriptService,
    public timeService: EventTimeService,
    public placeService: VisitorPlaceService,
    public pdfService: ExportPDFService,
    private renderer: Renderer2,
    private _snackBar: MatSnackBar,
    private _fb: FormBuilder,
    private _router: Router,
    private _store: Store<IAppState>
  ) {
    super({ activatedRoute });

    setTimeout(() => {
      console.log('going to add message listener for auth');
      window.addEventListener('message', (event) => {
        console.log('postMessage: ', event); // Log the origin if it's from the opener
        if (
          [
            'http://localhost:4200',
            'https://dev.imgoingcalendar.com',
            'https://imgoingcalendar.com',
          ].any((x) => x === event.origin)
        ) {
          if (event.data.data === 'REDIRECT_FROM_DXP') {
            this.isAuthenticated = true;
          }
        }

        /* One client mobile App wants to show a Check In button on the itinerary page, when visiting a place details.
         * The app will send a message to the itinerary page with the following data:
         * {type: 'CLIENT_APP', data: {type: 'ITINERARY_CHECK_IN', hubName: 'hubName', userId: 'userId'}}
         * When a place details page is loaded, it will post a message back to the APP via websocket
         * */
        if (event.type === 'CLIENT_APP' || event.data?.type === 'CLIENT_APP') {
          const data = event.data;
          const origin = event.origin;
          const type = event.type;
          const body = {
            hubName: this.hubName,
            source: 'itinerary: ' + origin,
            level: 'info',
            message: JSON.stringify(data) + ' - type: ' + type,
            exception: '',
            note: 'test communicate with app',
            createdAt: Date,
          };

          this.visitorService.logToDB(body).subscribe((res) => {});

          if (event.data?.data?.type === 'ITINERARY_CHECK_IN') {
            window['CLIENT_APP_MESSAGE'] = event.data.data;
          }

          // window.postMessage({
          //   type: 'CLIENT_APP',
          //   data: { type: 'ITINERARY_CHECK_IN', hubName: 'FrancesMarionTrail', userId: 'xxx' },
          // });
        }
      });
    });

    this.mediaQueryService.isMobile$.pipe(takeUntil(this.isDestroyed)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.isLoading$ = this._store.pipe(select(frontendSettingsIsLoadingSelector));
    this.error$ = this._store.pipe(select(frontendSettingsErrorSelector));
    this.frontendSettings$ = this._store.pipe(select(frontendSettingsSelector));

    this._store.dispatch(FrontendSettingsActions.getFrontendSettings({ hubName: this.hubName }));

    this.frontendSettings$.subscribe((settings: IFrontendSettings) => {
      if (settings) {
        this.itineraryMultiWidgetSettings = this.frontendSettings.tour.multiWidgetSettings || [
          {
            title: 'Default Widget',
            code: 'default',
            settings: clone(this.frontendSettings.tour.settings),
          },
        ];

        // TODO: need to think about the compatibility with current tour settings data.

        // for single itinerary view, set the widgetCode to null, then in the subscription of loadItineraries,
        // will set the widgetItineraries to allItineraries
        if (this._router.url.includes(routeConfigs.tripPlanner.singleTour.path.split(':')[0])) {
          this.widgetCode = null;
        } else {
          // load specific widget settings and itineraries
          this.widgetCode = this.allRouteParams['widgetCode'];

          if (!this.widgetCode || this.widgetCode.toLowerCase() === 'edit') {
            this.widgetCode = 'default';
          }
        }

        if (this.widgetCode) {
          const widget = this.itineraryMultiWidgetSettings.find((x) => x.code === this.widgetCode);
          if (widget) {
            this.itinerarySettings = clone(widget.settings);
          }
        }

        if (window.location.href.includes('/' + routeConfigs.tripPlanner.itinerary.editMode.path)) {
          this.isEditMode = true;
          this.loadItineraries(this.isEditMode);
        } else {
          this.loadItineraries(false, true);
        }
      }
    });

    this.subscribeDetailsModalClose();
  }

  ngOnInit(): void {
    this.updateOnlineStatus();

    // Event listeners for changes in connection status
    window.addEventListener('online', this.updateOnlineStatus);
    window.addEventListener('offline', this.updateOnlineStatus);
  }

  ngAfterViewInit() {}

  override ngOnDestroy() {
    // Clean up the scroll event listener when the directive is destroyed
    if (this.scrollListener) {
      this.scrollListener();
    }

    this.modalCloseSubscription && this.modalCloseSubscription.unsubscribe();
  }

  subscribeDetailsModalClose() {
    // receive from itinerary-details-modal in edit mode to reload all itineraries
    this.modalCloseSubscription = TripPlannerService.modalClose$.subscribe((data) => {
      if (!data) return;

      if (data.type === 'EDIT_MODE_CLOSE_DETAILS_MODAL') {
        this.loadItineraries(true);
      }
      if (data.type === 'RELOAD_3DMAP_ITINERARY') {
        this.enterItinerary(this.currentItinerary._id);
      }
    });
  }

  private checkVisibleChildren() {
    const containerRect = this.listContainer?.nativeElement.getBoundingClientRect();
    const children = Array.from(
      this.listContainer?.nativeElement.querySelectorAll('.ig-itinerary-card')
    ) as HTMLElement[];
    const visibleElements = children.filter((child) => {
      const childRect = child.getBoundingClientRect();
      return (
        childRect.left >= containerRect.left - 50 && childRect.right <= containerRect.right + 50
      );
    });

    if (visibleElements.length) {
      const itiId = visibleElements[0].getAttribute('itinerary-id');
      this.itineraryDescription.nativeElement.innerText = this.widgetItineraries.find(
        (x) => x._id === itiId
      ).description;
    } else {
      this.itineraryDescription.nativeElement.innerText = '';
    }
  }

  updateOnlineStatus() {
    if (navigator.onLine) {
      console.log('You are online!');
      // Perform actions when online, like syncing data or notifying the user
    } else {
      console.log('You are offline.');
      // Handle offline scenario, like showing offline messages or saving data locally
    }
  }

  loadItineraries(isGetAll = false, isOpenItineraryIfIdInUrl = false) {
    this.isLoadingItineraries = true;
    const observable = isGetAll
      ? this.tripService.getAllItineraries(this.hubName)
      : this.tripService.getActiveItineraries(this.hubName);
    observable.pipe(finalize(() => (this.isLoadingItineraries = false))).subscribe((data) => {
      // Initialize selectedWidgets for each itinerary
      data.forEach((itinerary) => {
        // Set default widget code if empty
        if (!itinerary.widgetCodes?.length) {
          itinerary.widgetCodes = ['default'];
        }

        // Initialize checkbox states
        this.selectedWidgets[itinerary._id] = {};
        this.itineraryMultiWidgetSettings.forEach((widget) => {
          this.selectedWidgets[itinerary._id][widget.code] = itinerary.widgetCodes.includes(
            widget.code
          );
        });
      });

      this.allItineraries = data;

      this.widgetItineraries = this.widgetCode
        ? data.filter((itinerary) => itinerary.widgetCodes?.includes(this.widgetCode))
        : this.allItineraries;

      if (this.isMobile) {
        setTimeout(() => {
          if (this.scrollListener) this.scrollListener();
          // For mobileview, Add the scroll event listener to the container, so when the user scrolls one itinerary into the viewport, it will show the itinerary description
          this.scrollListener = this.renderer.listen(
            this.listContainer?.nativeElement,
            'scroll',
            () => {
              console.log('scroll');
              this.checkVisibleChildren();
            }
          );
          this.checkVisibleChildren(); // Initial check
        }, 1000);
      }

      if (isOpenItineraryIfIdInUrl) {
        let url = null;
        if (!window.location.href.includes('ig-itinerary=')) {
          // check if itinerary id is in the embedding script
          const itiInEmbeddingScript = this.getScriptValue('route');
          url = new URL(`https://ig.com${itiInEmbeddingScript}`); // just to use URL to get the parameters
        } else {
          url = new URL(window.location.href);
        }

        const igItinerary = url.searchParams.get('ig-itinerary');
        const mode = url.searchParams.get('ig-mode');

        const itineraryIdFromV2Url = this.allRouteParams['itineraryId'];
        console.log('itineraryIdFromV2Url', itineraryIdFromV2Url);

        if (!igItinerary && !itineraryIdFromV2Url) return;

        const id = itineraryIdFromV2Url || igItinerary.split('-').last();
        const itinerary = this.widgetItineraries.find((x) => x._id === id);
        if (itineraryIdFromV2Url || mode === 'embed') {
          this.isForSingleItineraryEmbed = true;
        }
        setTimeout(() => {
          itinerary && this.enterItinerary(itinerary._id);
        }, 2000);
      }
    });
  }

  updateBgImage(url: string) {
    this.itinerarySettings.bgImage = url;
    this.updateSettings();
  }

  bgColorSlider($event) {
    this.itinerarySettings.bgColor = $event;
    // this.updateSettings();
  }

  updateSettings() {
    this.tripService
      .updateSettings(this.hubName, this.widgetCode, this.itinerarySettings)
      .subscribe(() => {
        this._snackBar.open(`All changes have been saved successfully.`, 'Close', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'ig-success-snackbar',
          duration: 4000,
        });
      });
  }

  get isShowTravelBuddy() {
    return (
      this.frontendSettings?.ai?.travelBuddy?.isActive &&
      (this.getScriptValue('travel-buddy') === 'true' ||
        this.frontendSettings.ai.travelBuddy.showOnAllClientWidgets ||
        !this.isScriptSolution)
    );
  }

  public get isNeedScroll() {
    if (!this.listContainer) return false;
    return (
      this.listContainer.nativeElement.offsetWidth < this.listContainer.nativeElement.scrollWidth
    );
  }

  hoverItineraryCard(itinerary: Itinerary) {
    setTimeout(() => {
      if (!itinerary.description || this.isMobile) return;
      setTimeout(
        () => {
          this.itineraryDescription.nativeElement.innerText = itinerary.description;
          this.itineraryDescription.nativeElement.style.transform = `translateY(0)`;
          this.itineraryDescription.nativeElement.style.opacity = 1;
          this.isDisplayingDescription = true;
        },
        this.isDisplayingDescription ? 400 : 0
      );
    }, 150);
  }

  swapItinerary(index: number) {
    if (index <= 0 || this.isUpdatingItineraries) return;
    this.isUpdatingItineraries = true;

    const itineraryArray = this.itineraryCards.toArray();

    itineraryArray[index].nativeElement.style.transition = `linear all 200ms`;
    itineraryArray[index - 1].nativeElement.style.transition = `linear all 200ms`;

    const box1 = itineraryArray[index - 1].nativeElement.getBoundingClientRect();
    const box2 = itineraryArray[index].nativeElement.getBoundingClientRect();
    const distance = box2.x - box1.x;
    itineraryArray[index].nativeElement.style.transform = `translateX(-${distance}px)`;
    itineraryArray[index - 1].nativeElement.style.transform = `translateX(${distance}px)`;

    setTimeout(() => {
      itineraryArray[index].nativeElement.style.transition = `none`;
      itineraryArray[index - 1].nativeElement.style.transition = `none`;
      [this.widgetItineraries[index - 1], this.widgetItineraries[index]] = [
        this.widgetItineraries[index],
        this.widgetItineraries[index - 1],
      ];

      itineraryArray[index].nativeElement.style.transform = `translateX(0px)`;
      itineraryArray[index - 1].nativeElement.style.transform = `translateX(0px)`;

      const tempOrder = this.widgetItineraries[index].order;
      this.widgetItineraries[index].order = this.widgetItineraries[index - 1].order;
      this.widgetItineraries[index - 1].order = tempOrder;
      // this.tours.sort((a, b) => a.order - b.order);

      this.tripService.updateItineraries(this.hubName, this.widgetItineraries).subscribe((data) => {
        this.isUpdatingItineraries = false;
        // this.alertify.successfully('Change itinerary order');
      });
    }, 220);
  }

  leaveItineraryCard() {
    this.itineraryDescription.nativeElement.style.transform = `translateY(20px)`;
    this.itineraryDescription.nativeElement.style.opacity = 0;
    setTimeout(() => {
      this.isDisplayingDescription = false;
    }, 300);
  }

  askAndEnterItinerary(itinerary: Itinerary) {
    if (!this.isEditMode && itinerary.detailsStyle === 'map3d') {
      this.promptMapModeChange(itinerary);
    } else {
      this.enterItinerary(itinerary._id);
    }
  }

  confirmMapMode(mode: 'map' | 'story') {
    if (mode) {
      this.getItineraryDetails(this.enteringItineraryId, mode);
      this.modeChangeMenuTrigger.closeMenu();
    } else {
      // continue with 3d map
      this.enterItinerary(this.enteringItineraryId);
    }
  }

  private promptMapModeChange(itinerary: Itinerary) {
    this.enteringItineraryId = itinerary._id;
    // this.modeChangeMenuTrigger.openMenu();
  }

  enterItinerary(itineraryId: string): void {
    // if (this.enteringItineraryId) return;

    this.enteringItineraryId = itineraryId;
    this.getItineraryDetails(itineraryId);

    return;

    // const card = this.itineraryCards.find(
    //   (x) =>
    //     x.nativeElement.attributes['itinerary-id']['nodeValue'] ===
    //     itinerary._id
    // );
    //
    // console.log('card', card);
    // if (card) {
    //   const img = card.nativeElement.querySelector('img');
    //   console.log('img', img);
    //   if (img) {
    //     const bgBox = this.backgroundDiv.nativeElement.getBoundingClientRect();
    //     const box = img.getBoundingClientRect();
    //     this.imgPositionInListView = box;
    //     const flyingImg = document.createElement('img');
    //
    //     const height = (bgBox.height * box.width) / bgBox.width;
    //
    //     flyingImg.src = img.src;
    //     flyingImg.style.position = 'absolute';
    //     flyingImg.style.borderRadius = '16px';
    //     flyingImg.style.objectFit = 'cover';
    //     flyingImg.style.left = `${box.left}px`;
    //     flyingImg.style.top = `${box.top}px`;
    //     flyingImg.style.width = `${box.width}px`;
    //     flyingImg.style.height = `${height}px`;
    //     flyingImg.style.transition = `all ${this.duration}ms linear`;
    //     document.body.appendChild(flyingImg);
    //     this.bgImg = flyingImg;
    //     this.isStartEntering = true;
    //
    //     setTimeout(() => {
    //       flyingImg.style.width = '100%';
    //       flyingImg.style.height = '100%';
    //       flyingImg.style.left = '0';
    //       flyingImg.style.top = '0';
    //
    //       setTimeout(() => {
    //         this.bgImg.style.borderRadius = '0px';
    //         // this.isStartEntering = false;
    //       }, this.duration);
    //     });
    //   }
    // }
  }

  getItineraryDetails(id: string, tempDetailsStyle?: 'map' | 'story') {
    this.isStartEntering = true;
    forkJoin([
      this.tripService.getItineraryDetails(this.hubName, id),
      this.googleMapService.loadGoogleMapApi(),
    ]).subscribe(([itinerary, isLoaded]) => {
      this.enteringItineraryId = null;
      itinerary.attractions = itinerary.attractions.filter((x) => !!x.item);
      this.currentItinerary = itinerary;
      this.itineraryDetailStyle = tempDetailsStyle || itinerary.detailsStyle;
      this.mapStyle = itinerary.mapStyle;
      this.isStartEntering = false;

      const url = new URL(window.location.href);
      const mode = url.searchParams.get('ig-mode');
      // isForSingleItineraryEmbed can be set in embedding script too
      if (this.isForSingleItineraryEmbed || mode === 'embed') {
        this.isForSingleItineraryEmbed = true;
        return;
      }

      const dialog = this.modalService.show({
        component: ItineraryDetailsModalComponent,
        panelClass: ['ig-modal-w-full', 'ig-iti-details-modal'],
        data: {
          currentItinerary: this.currentItinerary,
          tempDetailsStyle: this.isEditMode ? undefined : tempDetailsStyle,
          isEditMode: this.isEditMode,
        },
        disableClose: true,
      });

      setTimeout(() => {
        Array.from(this.attractionCards).forEach((x, i) => {
          this.expandingAttractions[i] = false;
          // this.addHoverLargerEffect(x.nativeElement, i);
        });
        this.isFoldItineraryTitle = false;
        // this.sendMessageToIframe({
        //   type: 'LOAD_ITINERARY',
        //   hubName: this.hubName,
        //   itineraryId: this.currentItinerary._id
        // });
      }, 2500);
    });
  }

  getLink(itinerary: Itinerary) {
    if (!itinerary.isActive) {
      this.link = `Unable to generate a link for an unpublished itinerary!`;
      return;
    }
    const nameId = this.seoService.getNameId(itinerary);
    const url = new URL(window.location.href);
    url.searchParams.set('ig-itinerary', nameId);
    this.link = url.href.replace('/' + routeConfigs.tripPlanner.itinerary.editMode.path, '');
  }

  getEmbeddingScript(itinerary: Itinerary) {
    if (!itinerary.isActive) {
      return `Unable to generate a link for an unpublished itinerary!`;
    }
    const nameId = this.seoService.getNameId(itinerary);
    return `<div id="imgoingcalendar-wrapper" route="/${this.hubName}/itineraries?ig-itinerary=${nameId}&ig-mode=embed" url-change="true">
                <app-root></app-root>
                <script type="text/javascript" src="https://imgoingcalendar.com/imgoingscript.js"></script>
              </div> `;
  }

  copy(mode: 'popup' | 'embed', itinerary?: Itinerary) {
    this.visitorService.copyToClip(
      mode === 'popup' ? this.link : this.getEmbeddingScript(itinerary)
    );
    mode === 'popup' ? (this.popupLinkCopied = true) : (this.embedLinkCopied = true);
    setTimeout(() => {
      mode === 'popup' ? (this.popupLinkCopied = false) : (this.embedLinkCopied = false);
    }, 4000);
  }

  deleteItinerary(itinerary: Itinerary, event: ButtonTriggerEvent) {
    this.tripService.deleteItinerary(itinerary._id, this.hubName).subscribe((data) => {
      event.success();
      setTimeout(() => {
        this.loadItineraries(true);
      }, 3000);
    });
  }

  hasSelections(itineraryId: string): boolean {
    const widgets = this.selectedWidgets[itineraryId] || {};
    return Object.values(widgets).some((x) => x);
  }

  moveItineraryToWidgets(event: ButtonTriggerEvent, itineraryId: string) {
    const widgetSelections = this.selectedWidgets[itineraryId] || {};
    const selectedWidgetCodes = Object.entries(widgetSelections)
      .filter(([code, selected]) => selected)
      .map(([code]) => code);
    console.log('selectedWidgetCodes', selectedWidgetCodes);

    this.tripService
      .moveItineraryToWidgets(this.hubName, itineraryId, selectedWidgetCodes)
      .pipe(finalize(() => event.complete()))
      .subscribe((data) => {
        this._snackBar.open(`Itinerary has been moved to the widgets`, 'Close', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'ig-success-snackbar',
          duration: 4000,
        });

        this.loadItineraries(true);
      });
  }

  getItineraryCount(widgetCode: string) {
    return this.allItineraries.filter((x) => x.widgetCodes.includes(widgetCode)).length;
  }

  updateMapIcons() {
    let index = 1;
    this.mapMarkers = [];
    for (const attraction of this.currentItinerary.attractions) {
      const mapIcon = attraction.mapIcon || this.defaultMapIconUrl;
      this.mapMarkers.push({
        position: {
          lat: attraction.item.address?.lat,
          lng: attraction.item.address?.lng,
        },
        options: {
          // icon: icons[attraction.item.name],
          icon: {
            url: mapIcon,
            scaledSize: mapIcon.includes('iti-images.s3.amazonaws.com/imgs/map-icon')
              ? new google.maps.Size(40, 55)
              : undefined,
          },
          zIndex: 2,
        },
        title: attraction.item._id, // use this for showing map info window when hovering on list items, see hoverAttraction function
        data: attraction.item,
      } as IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>);

      // for the yellow order marker
      this.mapMarkers.push({
        position: {
          lat: attraction.item.address?.lat,
          lng: attraction.item.address?.lng,
        },
        options: {
          icon: {
            url: 'https://imgoingcalendar.com/assets/imgs/tp-marker-2.png',
            anchor: new google.maps.Point(30, 50),
            scaledSize: new google.maps.Size(25, 25),
          } as google.maps.Icon,
          zIndex: 10,
          // optimized: true,
        },
        label: {
          text: (index++).toString(),
          fontWeight: 'bold',
        },
      } as IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>);
    }

    setTimeout(() => {
      this.mapMask && (this.mapMask.nativeElement.style.opacity = 0);
    }, 1500);
    setTimeout(() => {
      this.mapMask && (this.mapMask.nativeElement.style.height = 0);
      this.mapMask && (this.mapMask.nativeElement.style.width = 0);
    }, 2000);

    this.isShowSaveAttractionChanges = true;
  }

  public checkIsAtStart() {
    if (!this.listContainer) return;
    setTimeout(() => {
      const { scrollLeft } = this.listContainer.nativeElement;
      this.isAtStart = scrollLeft === 0;
    }, 800);
  }

  public checkIsAtEnd() {
    if (!this.listContainer) return;
    setTimeout(() => {
      const { scrollWidth, scrollLeft, clientWidth } = this.listContainer.nativeElement;

      this.isAtEnd = scrollWidth - (scrollLeft + clientWidth) <= 10;
    }, 800);
  }

  public goRight() {
    const padding = 16;
    const cards = this.itineraryCards.toArray();
    const cardWidth = cards[0].nativeElement.getBoundingClientRect().width + padding;
    const count = this.listContainer.nativeElement.offsetWidth / cardWidth;
    const move = cardWidth * Math.floor(count) || cardWidth;

    this.listContainer.nativeElement.scrollLeft += move;

    this.checkIsAtStart();
    this.checkIsAtEnd();
  }

  public goLeft() {
    const padding = 16;
    const cards = this.itineraryCards.toArray();
    const cardWidth = cards[0].nativeElement.getBoundingClientRect().width + padding;
    const count = this.listContainer.nativeElement.offsetWidth / cardWidth;
    const move = cardWidth * Math.floor(count) || cardWidth;

    this.listContainer.nativeElement.scrollLeft -= move;

    this.checkIsAtStart();
    this.checkIsAtEnd();
  }

  getEle(selector) {
    return this.shadowRoot.querySelector(selector);
  }

  nextAttraction() {
    this.continue = true;
    this.pauseForever = false;
    this.showAerial();
  }

  backAttraction() {
    this.pauseForever = true;
    this.attractionIndexInTourSimulation -= 1;
    this.showAttractionPopup(this.attractionIndexInTourSimulation, true);
  }

  showYoutube() {
    if (this.getYoutubeLink(this.currentAttractionInTourSimulation.item as VisitorPlace)) {
      this.pauseForever = true;
      this.currentVideoType = 'youtube';

      let aerial = this.getEle('.ig-popup-aerial-video');
      aerial.classList.add(`opacity-0`);
      setTimeout(() => {
        aerial.classList.remove(`z-10`);
      }, 1000);
    }
  }

  showAerial() {
    if (this.getAerialVideo(this.currentAttractionInTourSimulation)) {
      this.currentVideoType = 'aerial';
      let aerial = this.getEle('.ig-popup-aerial-video');
      aerial.classList.add(`z-10`);
      aerial.classList.remove(`opacity-0`);
    }
  }

  public getYoutubeLink(place: VisitorPlace) {
    return this.placeService.getYoutubeLink(place);
  }

  public get360VideoLink(place: VisitorPlace) {
    return this.placeService.get360VideoLink(place);
  }

  /**
   * Preload place video or images
   * If the place has a video (landscape for desktop, portrait for mobile), preload the video
   * If not --> if the place has allowed images, preload the images
   * If not --> check if need to load the place details from Google again (more than 7 days), then preload the photos
   * @param place
   * @returns
   */
  // async getPreloadObversable(
  //   attraction: ItineraryAttraction
  // ): Promise<Observable<boolean | null>> {
  //   if (attraction.feedType === 'event') {
  //     return this.scriptService.preloadImages([attraction.item.cover.source]);
  //   }
  //
  //   const place = attraction.item as VisitorPlace;
  //   const video = this.getAerialVideo(attraction);
  //   console.log('video', !!video);
  //
  //   if (video) {
  //     return this.scriptService.preloadVideos([video]);
  //   }
  //
  //   const allowedImages =
  //     place.allowedGooglePhotos &&
  //     place.allowedGooglePhotos.slice(0, this.preloadImageCount);
  //
  //   console.log('allowedImages', allowedImages.length);
  //
  //   if (allowedImages && allowedImages.length) {
  //     return this.scriptService.preloadImages(allowedImages);
  //   }
  //
  //   const loadedEarlierThanThreshold =
  //     !place.googleDetailsLoadedAt ||
  //     this.timeService.getDiff(
  //       new Date(),
  //       new Date(place.googleDetailsLoadedAt)
  //     ) > 7;
  //
  //   console.log('loadedEarlierThanThreshold', loadedEarlierThanThreshold);
  //
  //   if (!loadedEarlierThanThreshold) {
  //     console.log('preload existing photos');
  //     const originalSources = place.photos
  //       .slice(0, this.preloadImageCount)
  //       .map((x) => {
  //         return this.placeService.getGoogleImgUrl(
  //           x.photo_reference,
  //           this.frontendSettings.customSettings.dataSource.googleKey
  //         );
  //       });
  //     return this.scriptService.preloadImages(originalSources);
  //   }
  //
  //   const loadPlace$ = this.placeService.getGooglePlaceDetails(
  //     place.id,
  //     place.hubName
  //   );
  //   const placeDetails = await firstValueFrom(loadPlace$);
  //   console.log('placeDetails', placeDetails);
  //   if (placeDetails.photos) {
  //     place.photos = placeDetails.photos;
  //     const originalSources = placeDetails.photos
  //       .slice(0, this.preloadImageCount)
  //       .map((x) => {
  //         return this.placeService.getGoogleImgUrl(
  //           x.photo_reference,
  //           this.frontendSettings.customSettings.dataSource.googleKey
  //         );
  //       });
  //     console.log('preload$ after details');
  //     return this.scriptService.preloadImages(originalSources);
  //   }
  //
  //   return of(null);
  // }

  getAerialVideo(place: ItineraryAttraction): string | undefined {
    if (place.feedType !== 'place') return undefined;

    const videoUrl = this.isMobile
      ? (place.item as VisitorPlace).aerialVideo?.portrait.url
      : (place.item as VisitorPlace).aerialVideo?.landscape.url;
    return videoUrl;
  }

  getImages(item: ItineraryAttraction): string[] {
    if (item.feedType === 'place') {
      const attraction = item.item as VisitorPlace;
      const allowedImages =
        attraction.allowedGooglePhotos &&
        attraction.allowedGooglePhotos.slice(0, this.preloadImageCount);

      if (allowedImages && allowedImages.length) {
        return allowedImages;
      }

      const originalSources = attraction.photos.slice(0, this.preloadImageCount).map((x) => {
        return this.placeService.getGoogleImgUrl(
          x.photo_reference,
          this.frontendSettings.customSettings.dataSource.googleKey
        );
      });

      return originalSources;
    }

    return [item.item.cover.source];
  }

  async showAttractionPopup(attractionIndex: number, showImmediately = false): Promise<void> {
    this.attractionIndexInTourSimulation = attractionIndex;
    const attraction = this.currentItinerary.attractions[attractionIndex];
    this.currentAttractionInTourSimulation = attraction;

    const start = await this.getAttractionPosition(attractionIndex);
    this.setPopupStartUpPosition(start);

    // back to previous attraction shows without animation
    if (showImmediately) {
      this.showPopupContent(attraction);
    } else {
      setTimeout(() => {
        this.showPopupContent(attraction);
      });
    }

    if (attraction.feedType === 'place' && this.getAerialVideo(attraction)) {
      await this.stoppableWait(12 * 1000);
    } else {
      const images = this.getImages(attraction);
      if (images.length) {
        await this.stoppableWait(Math.max(images.length * 1000 * 3, 6000));
      }
    }

    this.hidePopup(start);
  }

  async getAttractionPosition(attractionIndex: number) {
    const legs = this.directionResults.routes[0].legs;
    const leg = legs[attractionIndex];
    let lat = 0,
      lng = 0;
    if (attractionIndex === 0) {
      lat = legs[0].steps[0].path[0].lat();
      lng = legs[0].steps[0].path[0].lng();
    } else if (attractionIndex === this.currentItinerary.attractions.length - 1) {
      lat = this.directionResults.request.destination['location'].lat();
      lng = this.directionResults.request.destination['location'].lng();
    } else {
      lat = leg.start_location.lat();
      lng = leg.start_location.lng();
    }

    const start = await this.googleMap.getPixelPositionByLatLng(new google.maps.LatLng(lat, lng));
    return start;
  }

  setPopupStartUpPosition(start: { x; y }) {
    this.attractionPopup.nativeElement.style.transition = 'none 0ms linear';

    this.attractionPopup.nativeElement.style.left = `${start.x}px`;
    this.attractionPopup.nativeElement.style.top = `${start.y}px`;
    this.attractionPopup.nativeElement.classList.remove('hidden');

    this.attractionPopup.nativeElement.style.width = '50px';
    this.attractionPopup.nativeElement.style.height = '40px';
    this.attractionPopup.nativeElement.classList.add('opacity-100');

    this.attractionPopup.nativeElement.style.transition = `all 300ms linear`;
  }

  hidePopup(hideAt: { x; y }) {
    let descContainer = this.attractionPopup.nativeElement.querySelector('[info]');
    descContainer && (descContainer.style.display = 'none');

    this.attractionPopup.nativeElement.classList.remove('opacity-100');
    this.attractionPopup.nativeElement.style.width = '50px';
    this.attractionPopup.nativeElement.style.height = '40px';
    this.attractionPopup.nativeElement.style.left = `${hideAt.x}px`;
    this.attractionPopup.nativeElement.style.top = `${hideAt.y}px`;
    setTimeout(() => {
      this.attractionPopup.nativeElement.classList.add('hidden');
    }, 300);
  }

  showPopupContent(attraction) {
    this.attractionPopup.nativeElement.style.width = '80%';
    this.attractionPopup.nativeElement.style.height = 'auto';
    this.attractionPopup.nativeElement.style.left = `10%`;
    this.attractionPopup.nativeElement.style.top = `30%`;
    this.attractionPopup.nativeElement.classList.add('opacity-100');

    if (attraction.feedType === 'place') {
      const videoUrl = this.getAerialVideo(attraction);
      if (videoUrl) {
        const video = this.attractionPopup.nativeElement.querySelector('video');
        video.classList.add('opacity-100');
      }
    }

    setTimeout(() => {
      let descContainer = this.attractionPopup.nativeElement.querySelector('[info]');
      if (descContainer) {
        descContainer.querySelector('[description]').innerText = removeHtmlTags(
          this.currentAttractionInTourSimulation.item.description
        );
        descContainer.style.display = 'block';
      }
    }, 300);
  }

  drawRouteLine(data: {
    angle: number;
    distance: number;
    start: { lat; lng };
    end?: { lat; lng };
  }) {
    const flightPlanCoordinates = [
      { lat: data.start.lat, lng: data.start.lng },
      { lat: data.end.lat, lng: data.end.lng },
    ];
    const flightPath = new google.maps.Polyline({
      path: flightPlanCoordinates,
      geodesic: true,
      strokeColor: 'gold', //'#b5ca2d',//'#0064b6',
      // strokeOpacity: 0.8,
      strokeWeight: 3,
    });

    flightPath.setMap(this.googleMap.googleMap.googleMap);
    this.directionLines.push(flightPath);
  }

  addMarkersForStep(path, i: number) {
    this.mapMarkers.push({
      position: {
        lat: path.lat(),
        lng: path.lng(),
      },
      options: {
        icon: {
          url: 'https://imgoingcalendar.com/assets/imgs/tp-marker-2.png',
          scaledSize: new google.maps.Size(18, 18),
        } as google.maps.Icon,
        zIndex: 10,
        // optimized: true,
      },
      label: {
        text: i.toString(),
        fontWeight: 'bold',
      },
    } as IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>);
  }

  // Function to calculate angle between two points
  // calculateAngleAndDistance(point1, point2) {
  //   const dx = point2.x - point1.x;
  //   const dy = point2.y - point1.y;
  //
  //   // Calculate angle using atan2 function
  //   const angleInRadians = Math.atan2(dy, dx);
  //   // Convert angle to degrees and adjust for 360-degree reference
  //   const angleInDegrees = (angleInRadians * 180) / Math.PI;
  //   const angle = angleInDegrees + 90; // Adjust for north reference
  //
  //   const mostSimilarAngle = this.getMostSimilarAngle(angle);
  //
  //   return {
  //     angle,
  //     mostSimilarAngle,
  //     distance: Math.sqrt(dx * dx + dy * dy), // Calculate difference in x and y coordinates
  //   };
  // }

  // getMostSimilarAngle(angle: number) {
  //   const targetAngles = [];
  //   for (let i = -165; i < 270; i += 15) {
  //     targetAngles.push(i);
  //   }
  //   // const targetAngles = [
  //   //   -165, -150, -135, -120, -105, -90, -75, -60, -45, -30, -15, 0, 15, 30, 45,
  //   //   60, 75, 90, 105, 120, 135, 150, 165, 180, 195, 210, 225, 240, 255, 270,
  //   // ];
  //   // calcule the most similiar angle
  //   const mostSimilarAngle = targetAngles.reduce((prev, curr) =>
  //     Math.abs(curr - angle) < Math.abs(prev - angle) ? curr : prev
  //   );
  //   return mostSimilarAngle;
  // }

  // async moveCarTo(point, duration = 1000) {
  //   const car = this.car360;
  //   car.nativeElement.style.transition = `left ${duration}ms linear,top ${duration}ms linear`;
  //
  //   this.left = point.x - this.carOffset;
  //   this.top = point.y + this.carOffset;
  //
  //   await this.myWait(duration);
  // }

  // removeCardRotate() {
  //   // remove rotate effect for attraction cards, otherwise it blocks the drag/drop
  //   setTimeout(() => {
  //     for (const card of this.attractionCards) {
  //       this.renderer.removeClass(card.nativeElement, 'ig-rotate-180');
  //     }
  //   }, 3000);
  // }

  private leftInAnimate(letters: NodeListOf<Element>) {
    window['anime']
      .timeline({ loop: false })
      .add({
        targets: letters,
        translateX: [40, 0],
        opacity: [0, 1],
        easing: 'easeOutExpo',
        duration: 1200,
        delay: (el, i) => 500 + 30 * i,
      })
      .add({
        targets: letters,
        translateX: [0, -10],
        opacity: [1, 0],
        easing: 'easeInExpo',
        duration: 2100,
        delay: (el, i) => 100 + 10 * i,
      });
  }

  myWait(milSeconds) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(null);
      }, milSeconds);
    });
  }

  continue = false;
  pauseForever = false;

  async stoppableWait(waitTime: number) {
    let wait = 0;
    while ((!this.continue && wait <= waitTime) || this.pauseForever) {
      await this.myWait(100);
      wait += 110; // setTimeout is not accurate, add some margin
    }
    this.continue = false;
  }
}
